const REGULATION_COUNTRY_PREFIX_PATTERN = /^(US|CANADA)_/

// Kambi stores regulation identifiers as machine codes (for example
// US_ARIZONA or CANADA_ONTARIO). Studio labels the operator-recognisable place,
// while preserving already-human labels supplied by newer mappings.
export function formatRegulationName(value) {
  const normalized = String(value || '').trim()
  if (!normalized) return ''
  if (normalized === 'MEXICO') return 'Mexico'
  if (normalized === 'PERU') return 'Peru'
  if (!normalized.includes('_')) return normalized

  const words = normalized
    .replace(REGULATION_COUNTRY_PREFIX_PATTERN, '')
    .split('_')
    .filter(Boolean)
  return words.map((word) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')
}

// Regulation is the operator-facing noun; the provider's offering code is only
// ever a disambiguator. An unmapped code has no regulation name to show, so
// callers fall back to the code itself rather than labelling it "unknown".
export function buildRegulationLookup(mappings = []) {
  const byOffering = new Map(
    mappings
      .filter((mapping) => mapping?.offering_id)
      .map((mapping) => [mapping.offering_id, formatRegulationName(mapping.regulation_name)]),
  )
  return (offeringId) => byOffering.get(offeringId) || ''
}

// Command Center organization records and the per-org Kambi offerings payload
// name the same mapping list differently; readers must accept both or the UI
// drops every regulation name and regresses to raw offering codes.
export function organizationRegulationMappings(record) {
  return record?.regulation_mappings || record?.mappings || []
}

// The drawer's regulation rows and the composer's regulation list both need one
// unambiguous string per offering, because two codes can share a regulation.
export function regulationDisplayLabel(offeringId, regulation) {
  return regulation ? `${regulation} (${offeringId})` : String(offeringId || '')
}

// A book's own pickers name each regulation plainly and add the offering code
// only where two of its codes share a regulation (Delaware runs three), since
// the code is the book's internal identifier. entries: [{ offeringId, regulation }].
export function bookRegulationLabels(entries = []) {
  const nameCounts = new Map()
  for (const { regulation } of entries) {
    if (regulation) nameCounts.set(regulation, (nameCounts.get(regulation) || 0) + 1)
  }
  return new Map(entries.map(({ offeringId, regulation }) => [
    offeringId,
    !regulation ? String(offeringId || '')
      : nameCounts.get(regulation) > 1 ? regulationDisplayLabel(offeringId, regulation)
        : regulation,
  ]))
}

// A regulation is an offering, and an offering can be mapped under more than
// one operator (Sunbet's SUNBET/siwc and SUN/siwc, for PASTA). The server
// answers one mapping per offering (services.RegulationBooks); this keeps a
// list that still carries both as one book, since two books on one offering
// are two identical lineup reads, which TanStack's useQueries never settles
// (#1184). The same rule as the server's: the default mapping stands, else the
// first that names a regulation, else the first; it keeps its own name, else
// takes the first among the others, and the search donor is the first in the
// list's order whichever row stands.
export function regulationBooks(mappings = []) {
  const filled = (value) => Boolean(String(value || '').trim())
  const rank = (item) => (item.is_default ? 2 : filled(item.regulation_name) ? 1 : 0)
  const firstFilled = (...values) => values.find(filled)
  const books = new Map()
  for (const mapping of Array.isArray(mappings) ? mappings : []) {
    const id = mapping?.offering_id
    if (!id) continue
    const current = books.get(id)
    if (!current) {
      books.set(id, mapping)
      continue
    }
    const [chosen, other] = rank(mapping) > rank(current) ? [mapping, current] : [current, mapping]
    const name = firstFilled(chosen.regulation_name, other.regulation_name) ?? chosen.regulation_name
    // current already carries the first donor of the rows before.
    const donor = firstFilled(current.search_offering_id, mapping.search_offering_id) ?? chosen.search_offering_id
    books.set(id, name === chosen.regulation_name && donor === chosen.search_offering_id ? chosen : { ...chosen, regulation_name: name, search_offering_id: donor })
  }
  return [...books.values()]
}

// Offering codes sort by provider prefix, which reads as random to an operator
// scanning for a place. Sorting the full `Name (code)` label keeps same-name
// codes together in code order, and an unmapped code sorts by itself.
export function sortRegulationsByLabel(regulations = []) {
  return [...regulations].sort((a, b) => (
    String(a?.label || '').localeCompare(String(b?.label || ''), undefined, { sensitivity: 'base' })
  ))
}

// How much of an assigned regulation list is selected, with the summary a
// compact or folded picker shows for it ("All 3 selected", "2 of 3 selected").
// `noneLabel` names an empty selection, which callers read differently: no
// target, or unrestricted.
export function regulationSelection(selectedCount, availableCount, { noneLabel = 'None selected' } = {}) {
  if (selectedCount === 0) return { state: 'none', summary: noneLabel }
  if (selectedCount === availableCount) return { state: 'all', summary: availableCount === 1 ? '1 selected' : `All ${availableCount} selected` }
  return { state: 'some', summary: `${selectedCount} of ${availableCount} selected` }
}

// Prepacks names a regulation by its offering code or by a regulation record
// from the regulations endpoint. A code is labelled from its record in
// `regulations` when there is one.
export function regulationId(regulation) {
  if (typeof regulation === 'string') return regulation.trim()
  return regulation?.offering_id || regulation?.id || regulation?.regulation || ''
}

export function regulationLabel(regulation, regulations = []) {
  if (typeof regulation === 'string') {
    const record = regulations.find((item) => (item?.offering_id || item?.id || item?.regulation) === regulation)
    return formatRegulationName(record?.regulation_name || record?.name || regulation) || regulation
  }
  const id = regulationId(regulation)
  return formatRegulationName(regulation?.regulation_name || regulation?.name || id) || id
}

export function searchRegulationName(offeringId, mappings = []) {
  // Search answers with lower-cased offering codes; mappings keep their own case.
  const code = String(offeringId || '').toLowerCase()
  const mapping = code ? mappings.find((item) => String(item.offering_id || '').toLowerCase() === code) : undefined
  // This compact note has no code disambiguator. Missing mapping labels use
  // generic local-price copy, without exposing a provider ID to operators.
  const name = mapping?.regulation_name
  return name && name.toLowerCase() !== code ? formatRegulationName(name) : ''
}

// Older Sunbet onboarding rows have no regulation_name. Keep their public
// regulation label here until those source rows carry it themselves.
export function sportsSearchRegulationLabel(row = {}) {
  const regulation = row.regulation_name || (row.offering_id === 'siwc' ? 'South Africa' : '')
  const label = formatRegulationName(regulation || row.label || row.offering_id)
  return row.offering_id?.endsWith('rl') ? `${label} · Retail` : label
}

export function defaultSearchOffering(offerings = []) {
  return offerings.find((offering) => offering.is_default) || offerings[0]
}

export function isSearchOfferingAvailable(offering, { allowBorrowed = true } = {}) {
  return offering?.search_status === 'indexed'
    || offering?.search_status === 'alias'
    || (allowBorrowed && offering?.search_status === 'borrowed')
}
